import { HttpClient, HttpEvent, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map, Observable, switchMap } from "rxjs";
import { ExtendedFile } from "src/app/shared/interfaces";

import {
  AnalysisResult,
  DocumentResultResponse,
  DocumentUploadResponse,
  FileSizeLimitResponse,
  InitializeUploadResponse,
  NormalizedTranscription,
  ProbeUrlResponse,
  ProcessAnalysisOptions,
  SupportedFileTypesResponse,
  TableData,
} from "./document.model";

@Injectable()
export class DocumentService {
  private http = inject(HttpClient);

  analyzeDocument(file: ExtendedFile): Observable<DocumentUploadResponse> {
    const formData = new FormData();
    formData.append("file", file);

    if (file.modelType === "Pandoc") {
      return this.http.post<DocumentUploadResponse>(
        "api/v1/pandoc-convert",
        formData,
        {
          params: { spinner: "no" },
        }
      );
    }

    let params: HttpParams = new HttpParams()
      .set("spinner", "no")
      .set("model", file.modelType || "");

    if (file.outputFormat) {
      params = params.set("outputContentType", file.outputFormat);
    }

    if (file.includeLineNumbers) {
      params = params.set("includeLineNumbers", "true");
    }

    if (file.enableComments) {
      params = params.set("enableComments", "true");
    }

    return this.http.post<DocumentUploadResponse>("api/v1/analyze", formData, {
      params,
    });
  }

  getDocumentResult(
    id: string,
    modelType?: string
  ): Observable<DocumentResultResponse> {
    if (modelType === "Pandoc") {
      return this.http.get<DocumentResultResponse>(
        `api/v1/pandoc-convert/${id}/results`,
        {
          params: { spinner: "no" },
        }
      );
    }

    return this.http.get<DocumentResultResponse>(
      `api/v1/analyze/${id}/results`,
      {
        params: { spinner: "no" },
      }
    );
  }

  // Step 1 of the v2 flow: reserve a request and obtain a write-only SAS URL.
  initializeUpload(file: ExtendedFile): Observable<InitializeUploadResponse> {
    return this.http.post<InitializeUploadResponse>(
      "api/v2/analyze/initialize-upload",
      {
        fileName: file.name,
        fileSizeBytes: file.size,
        contentType: file.type || null,
      },
      { params: { spinner: "no" } }
    );
  }

  // Step 2 of the v2 flow: upload bytes straight to blob storage. The SAS URL is
  // absolute, so authInterceptor leaves it untouched (no bearer token / cookies),
  // which is what Azure Blob SAS authentication requires.
  // Emits HttpEvents (UploadProgress + final Response) so callers can show
  // real upload progress for large files.
  uploadToSas(
    uploadUrl: string,
    file: ExtendedFile
  ): Observable<HttpEvent<unknown>> {
    const headers: Record<string, string> = {
      "x-ms-blob-type": "BlockBlob",
    };
    if (file.type) {
      headers["Content-Type"] = file.type;
    }
    return this.http.put(uploadUrl, file, {
      headers,
      params: { spinner: "no" },
      reportProgress: true,
      observe: "events",
    });
  }

  // Probes a user-supplied source URL server-side and returns its reported
  // size/content-type, used to auto-fill the size field in URL mode.
  probeUrl(sourceUrl: string): Observable<ProbeUrlResponse> {
    return this.http.post<ProbeUrlResponse>(
      "analyze/probe-url",
      { sourceUrl },
      { params: { spinner: "no" } }
    );
  }

  copyFromUrl(
    uploadUrl: string,
    sourceUrl: string,
    contentType?: string
  ): Observable<HttpEvent<unknown>> {
    const headers: Record<string, string> = {
      "x-ms-blob-type": "BlockBlob",
      "x-ms-copy-source": sourceUrl,
    };
    if (contentType) {
      headers["x-ms-blob-content-type"] = contentType;
    }
    return this.http.put(uploadUrl, null, {
      headers,
      params: { spinner: "no" },
      observe: "events",
    });
  }

  // Step 3 of the v2 flow: validate the upload and enqueue it for analysis.
  // Options come from the action (the uploaded file is frozen in the store, so
  // its model fields can't be set there).
  processUpload(
    requestId: string,
    options: ProcessAnalysisOptions
  ): Observable<void> {
    const body: Record<string, unknown> = {
      model: options.model || "Basic",
      includeLineNumbers: options.includeLineNumbers,
      enableComments: options.enableComments,
    };
    if (options.outputContentFormat) {
      body["outputContentFormat"] = options.outputContentFormat;
    }
    return this.http.post<void>(`api/v2/analyze/${requestId}/process`, body, {
      params: { spinner: "no" },
    });
  }

  getDocumentResultV2(id: string): Observable<DocumentResultResponse> {
    return this.http.get<DocumentResultResponse>(
      `api/v2/analyze/${id}/results`,
      {
        params: { spinner: "no" },
      }
    );
  }

  // v2 returns results inline only below a size cap; above it, the full results
  // JSON is downloaded from this SAS URI (mirrors getDocumentTablesResult).
  getDocumentResultsFromBlob(
    resultsBlobUri: string
  ): Observable<AnalysisResult> {
    return this.http
      .get<Blob>(resultsBlobUri, {
        params: { spinner: "no" },
        responseType: "blob" as "json",
      })
      .pipe(
        switchMap(blob => blob.text()),
        map(text => JSON.parse(text) as AnalysisResult)
      );
  }

  getSupportedFileTypes(): Observable<SupportedFileTypesResponse> {
    return this.http.get<SupportedFileTypesResponse>(
      "analyze/supportedFileTypes",
      {
        params: { spinner: "no" },
      }
    );
  }

  getMaxFileSizeBytes(): Observable<FileSizeLimitResponse> {
    return this.http.get<FileSizeLimitResponse>("analyze/fileSizeLimit", {
      params: { spinner: "no" },
    });
  }

  getDocumentWordsResult(wordsBlobUri: string): Observable<Blob> {
    return this.http.get<Blob>(wordsBlobUri, {
      responseType: "blob" as "json",
    });
  }

  getDocumentTablesResult(tableBlobUri: string): Observable<TableData[]> {
    return this.http
      .get<Blob>(tableBlobUri, {
        responseType: "blob" as "json",
      })
      .pipe(
        switchMap(blob => blob.text()),
        map(text => JSON.parse(text) as TableData[])
      );
  }

  getDocumentConvertedPdf(pdfBlobUri: string): Observable<Blob> {
    return this.http.get(pdfBlobUri, { responseType: "blob" });
  }

  getTranscriptionResult(
    transcriptionBlobUri: string
  ): Observable<NormalizedTranscription> {
    return this.http
      .get<Blob>(transcriptionBlobUri, {
        responseType: "blob" as "json",
      })
      .pipe(
        switchMap(blob => blob.text()),
        map(text => JSON.parse(text) as NormalizedTranscription)
      );
  }

  getDocumentAudio(audioBlobUri: string): Observable<Blob> {
    return this.http.get(audioBlobUri, { responseType: "blob" });
  }
}
